import * as React from 'react'
import StageHeader from '../../shared/StageHeader'
import FormLayout from '../../shared/FormLayout'
import { Divider } from '../../shared/Divider'
import useContractFieldSetOptions from '../options/useContractFieldSetOptions'
import { useContractForm } from '@/modules/drivers/context/ContractFormContext'
import { RatesOption } from '@/modules/drivers/types/contracts'
import CheckboxWithLabelField from '../inputs/CheckboxWithLabelField'
import NumberFieldInnerTable from '../inputs/NumberFieldInnerTable'
import NumberAndSelectInnerField, {
  FieldAndInputProps,
} from '../inputs/NumberAndSelectInnerField'

import RateReduceCustom from '../components/shared/RateReduceCustom'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

const ContractAdditionalRates = () => {
  const { t } = useLocalize()
  const { setData, data } = useContractForm()
  const {
    contractNightOptions,
    contractDutyOptions,
    contractBonusOptions,
    contractRewardOptions,
    contractRatesOptions,
  } = useContractFieldSetOptions()

  const additionalRates: FieldAndInputProps[] = [
    {
      inputProps: {
        innerLabel: data?.duty50RateOption !== 1 ? 'PLN' : '[%]',
        value: data?.duty50Rate?.toString() ?? '',
        required: true,
        placeholder: '50',
        field: 'duty50Rate',
      },
      fieldOptions: {
        fullWidth: true,
        label: t('contract.duty-hours-50'),
      },
      selectProps: {
        field: 'duty50RateOption',
        options: contractRatesOptions,
        value: data?.duty50RateOption?.toString() ?? '',
        required: true,
        placeholder: 'Wybierz...',
      },
    },
    {
      inputProps: {
        innerLabel: data?.duty100RateOption !== 1 ? 'PLN' : '[%]',
        field: 'duty100Rate',
        value: data?.duty100Rate?.toString() ?? '',
        required: true,
        placeholder: '100',
      },
      fieldOptions: {
        fullWidth: true,
        label: t('contract.duty-hours-100'),
      },
      selectProps: {
        field: 'duty100RateOption',
        options: contractRatesOptions,
        value: data?.duty100RateOption?.toString() ?? '',
        required: true,
        placeholder: 'Wybierz...',
      },
    },
    {
      inputProps: {
        innerLabel: data?.nightHoursRateOption !== 1 ? 'PLN' : '[%]',
        field: 'nightHoursRate',
        value: data?.nightHoursRate?.toString() ?? '',
        required: true,
        placeholder: '20',
      },
      fieldOptions: {
        fullWidth: true,
        label: t('contract.night-hours'),
      },
      selectProps: {
        field: 'nightHoursRateOption',
        options: contractNightOptions,
        value: data?.nightHoursRateOption?.toString() ?? '',
        required: true,
        placeholder: 'Wybierz...',
      },
    },
  ]
  const Rewards: FieldAndInputProps[] = [
    {
      inputProps: {
        innerLabel: data?.bonusRateOption !== 1 ? 'PLN' : '[%]',
        field: 'bonusRate',
        value: data?.bonusRate?.toString() ?? '',
        required: true,
        placeholder: '0,00',
      },
      fieldOptions: {
        fullWidth: true,
        label: t('contract.bonus'),
      },
      selectProps: {
        field: 'bonusRateOption',
        options: contractBonusOptions,
        value: data?.bonusRateOption?.toString() ?? '',
        required: true,
        placeholder: 'Wybierz...',
      },
      children: (
        <RateReduceCustom
          data={data}
          setData={setData}
          reduceType="additional"
          background
          mainCheckboxProperty="reduceBonusRates"
          sickLeaveProperty="reduceBonusRatesForSickLeave"
          paidLeaveProperty="reduceBonusRatesForLeave"
          unpaidLeaveProperty="reduceBonusRatesForNotPaidLeave"
        />
      ),
    },
    {
      inputProps: {
        innerLabel: 'PLN',
        field: 'rewardRate',
        value: data?.rewardRate?.toString() ?? '',
        required: true,
        placeholder: '0,00',
      },
      fieldOptions: {
        fullWidth: true,
        label: t('contract.reward'),
      },
      selectProps: {
        field: 'rewardRateOption',
        options: contractRewardOptions,
        value: data?.rewardRateOption?.toString() ?? '',
        required: true,
        placeholder: 'Wybierz...',
      },
      children: (
        <RateReduceCustom
          data={data}
          setData={setData}
          reduceType="additional"
          background
          mainCheckboxProperty="reduceRewardRates"
          sickLeaveProperty="reduceRewardRatesForSickLeave"
          paidLeaveProperty="reduceRewardRatesForLeave"
          unpaidLeaveProperty="reduceRewardRatesForNotPaidLeave"
        />
      ),
    },
  ]
  React.useEffect(() => {
    if (!data) return
    if (
      data.duty50RateOption === undefined ||
      data.duty100RateOption === undefined ||
      data.nightHoursRateOption === undefined
    ) {
      setData((prevData) => ({
        ...prevData,
        duty50RateOption: (prevData.duty50RateOption ?? 50) as RatesOption,
        duty100RateOption: (prevData.duty100RateOption ?? 100) as RatesOption,
        nightHoursRateOption: (prevData.nightHoursRateOption ??
          20) as RatesOption,
      }))
    }
  }, [data])
  return (
    <div className="tw-flex tw-min-h-[500px] tw-justify-between">
      <StageHeader title={t('contract.additional-rates')} />
      <FormLayout>
        <>
          <div>
            <NumberFieldInnerTable
              inputSize="big"
              fullWidth
              label={t('contract.eu-work-addition')}
              innerLabel="PLN"
              placeholder="0,00"
              value={data?.euWorkRate?.toString() || ''}
              onChange={(e) => {
                setData({
                  ...data,
                  euWorkRate: Number(e.target.value) as RatesOption,
                })
              }}
            />
            <RateReduceCustom
              data={data}
              background
              setData={setData}
              reduceType="additional"
              mainCheckboxProperty="reduceEuWorkRates"
              sickLeaveProperty="reduceEuWorkRatesForSickLeave"
              paidLeaveProperty="reduceEuWorkRatesForLeave"
              unpaidLeaveProperty="reduceEuWorkRatesForNotPaidLeave"
            />
          </div>

          <Divider />
        </>
        <div className="tw-space-y-4">
          {additionalRates.map((q) => {
            return (
              <NumberAndSelectInnerField
                key={q.inputProps.field}
                inputProps={{
                  field: q.inputProps.field,
                  innerLabel: q.inputProps.innerLabel,
                  placeholder: q.inputProps.placeholder,
                  value: q.inputProps.value?.toString() || '',
                  onChange: (e) =>
                    setData({
                      ...data,
                      [q.inputProps.field]: Number(e.target.value),
                    }),
                }}
                fieldOptions={{
                  fullWidth: true,
                  label: q.fieldOptions.label,
                  inputSize: 'big',
                }}
                selectProps={{
                  field: q.selectProps.field,
                  required: true,
                  placeholder: q.selectProps.placeholder,
                  value: q.selectProps.value?.toString(),
                  onChange: (e) =>
                    setData({
                      ...data,
                      [q.selectProps.field]: Number(
                        e.target.value,
                      ) as RatesOption,
                    }),
                  options: q.selectProps.options,
                }}
              />
            )
          })}
        </div>
        <Divider />
        {Rewards.map((q) => {
          return (
            <div key={q.fieldOptions.label}>
              <NumberAndSelectInnerField
                inputProps={{
                  field: q.inputProps.field,
                  innerLabel: q.inputProps.innerLabel,
                  placeholder: '0,00',
                  value: q.inputProps.value?.toString() || '',
                  onChange: (e) =>
                    setData({
                      ...data,
                      [q.inputProps.field]: Number(e.target.value),
                    }),
                }}
                fieldOptions={{
                  fullWidth: true,
                  label: q.fieldOptions.label,
                  inputSize: 'big',
                }}
                selectProps={{
                  field: q.selectProps.field,
                  required: true,
                  placeholder: q.selectProps.placeholder,
                  value: q.selectProps.value?.toString(),
                  onChange: (e) =>
                    setData({
                      ...data,
                      [q.selectProps.field]: Number(
                        e.target.value,
                      ) as RatesOption,
                    }),
                  options: q.selectProps.options,
                }}
              />
              {q.children}
            </div>
          )
        })}
        <Divider />
        <CheckboxWithLabelField
          field="overtimeAlwaysAs100"
          data={data}
          setData={setData}
          label={t('contract.daily-overtime-as-100')}
        />
        {/* <RateReduceAdditional data={data} setData={setData} /> */}
      </FormLayout>
    </div>
  )
}

export default ContractAdditionalRates
